export type DockDirection = "horizontal" | "vertical";
export type DockPosition = "center" | "left" | "right" | "top" | "bottom";

export interface DockGroupNode {
  readonly type: "group";
  readonly id: string;
  readonly surfaceIds: readonly string[];
  readonly activeSurfaceId: string | null;
}

export interface DockSplitNode {
  readonly type: "split";
  readonly id: string;
  readonly direction: DockDirection;
  readonly ratio: number;
  readonly first: DockNode;
  readonly second: DockNode;
}

export type DockNode = DockGroupNode | DockSplitNode;

export interface DockLayout {
  readonly root: DockNode;
  readonly activeGroupId: string;
}

const MIN_SPLIT_RATIO = 0.15;
const MAX_SPLIT_RATIO = 0.85;
export const DOCK_HORIZONTAL_STACK_WIDTH = 720;
export const DOCK_DRAG_HOLD_MS = 180;
export const DOCK_DRAG_SLOP_PX = 7;

export function exceedsDockDragSlop(
  startX: number,
  startY: number,
  clientX: number,
  clientY: number,
): boolean {
  return Math.hypot(clientX - startX, clientY - startY) > DOCK_DRAG_SLOP_PX;
}

/**
 * A pair of side-by-side tools becomes unusable before the overall viewport
 * reaches a mobile breakpoint because the dock shares space with the main
 * sidebar and chat. Preserve the stored layout intent while rendering the
 * split as a vertical stack inside a compact dock.
 */
export function resolveDockRenderDirection(
  direction: DockDirection,
  workspaceWidth: number,
): DockDirection {
  return direction === "horizontal" &&
    Number.isFinite(workspaceWidth) &&
    workspaceWidth < DOCK_HORIZONTAL_STACK_WIDTH
    ? "vertical"
    : direction;
}

export function createDockLayout(surfaceIds: readonly string[], groupId = "dock:root"): DockLayout {
  return {
    root: {
      type: "group",
      id: groupId,
      surfaceIds: [...surfaceIds],
      activeSurfaceId: surfaceIds.at(-1) ?? null,
    },
    activeGroupId: groupId,
  };
}

export function dockGroups(node: DockNode): readonly DockGroupNode[] {
  return node.type === "group" ? [node] : [...dockGroups(node.first), ...dockGroups(node.second)];
}

export function findDockGroup(node: DockNode, groupId: string): DockGroupNode | null {
  if (node.type === "group") return node.id === groupId ? node : null;
  return findDockGroup(node.first, groupId) ?? findDockGroup(node.second, groupId);
}

export function activeDockSurfaceId(layout: DockLayout): string | null {
  const activeGroup =
    findDockGroup(layout.root, layout.activeGroupId) ?? dockGroups(layout.root)[0] ?? null;
  return activeGroup?.activeSurfaceId ?? null;
}

function mapDockGroups(node: DockNode, mapper: (group: DockGroupNode) => DockGroupNode): DockNode {
  if (node.type === "group") return mapper(node);
  return {
    ...node,
    first: mapDockGroups(node.first, mapper),
    second: mapDockGroups(node.second, mapper),
  };
}

function replaceDockNode(node: DockNode, nodeId: string, replacement: DockNode): DockNode {
  if (node.id === nodeId) return replacement;
  if (node.type === "group") return node;
  return {
    ...node,
    first: replaceDockNode(node.first, nodeId, replacement),
    second: replaceDockNode(node.second, nodeId, replacement),
  };
}

export function clampDockSplitRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) return 0.5;
  return Math.max(MIN_SPLIT_RATIO, Math.min(MAX_SPLIT_RATIO, ratio));
}

function normalizeNode(node: DockNode): DockNode | null {
  if (node.type === "group") return node.surfaceIds.length > 0 ? node : null;
  const first = normalizeNode(node.first);
  const second = normalizeNode(node.second);
  if (!first) return second;
  if (!second) return first;
  return { ...node, ratio: clampDockSplitRatio(node.ratio), first, second };
}

/**
 * Reconciles a persisted tree against the authoritative surface registry.
 * A surface can appear in at most one group; unknown ids are removed and new
 * registry entries are appended to the active group.
 */
export function normalizeDockLayout(layout: DockLayout, surfaceIds: readonly string[]): DockLayout {
  const valid = new Set(surfaceIds);
  const seen = new Set<string>();
  let root = mapDockGroups(layout.root, (group) => {
    const ids = group.surfaceIds.filter((id) => {
      if (!valid.has(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return {
      ...group,
      surfaceIds: ids,
      activeSurfaceId:
        group.activeSurfaceId && ids.includes(group.activeSurfaceId)
          ? group.activeSurfaceId
          : (ids.at(-1) ?? null),
    };
  });

  const missing = surfaceIds.filter((id) => !seen.has(id));
  if (missing.length > 0) {
    const target = findDockGroup(root, layout.activeGroupId) ??
      dockGroups(root)[0] ?? {
        type: "group" as const,
        id: layout.activeGroupId || "dock:root",
        surfaceIds: [],
        activeSurfaceId: null,
      };
    if (dockGroups(root).length === 0) {
      root = target;
    }
    root = mapDockGroups(root, (group) =>
      group.id === target.id
        ? {
            ...group,
            surfaceIds: [...group.surfaceIds, ...missing],
            activeSurfaceId: missing.at(-1) ?? group.activeSurfaceId,
          }
        : group,
    );
  }

  const normalized = normalizeNode(root) ?? createDockLayout([], "dock:root").root;
  const groups = dockGroups(normalized);
  const activeGroupId = groups.some((group) => group.id === layout.activeGroupId)
    ? layout.activeGroupId
    : (groups[0]?.id ?? "dock:root");
  return { root: normalized, activeGroupId };
}

export function activateDockSurface(layout: DockLayout, surfaceId: string): DockLayout {
  const group = dockGroups(layout.root).find((entry) => entry.surfaceIds.includes(surfaceId));
  if (!group) return layout;
  return {
    root: mapDockGroups(layout.root, (entry) =>
      entry.id === group.id ? { ...entry, activeSurfaceId: surfaceId } : entry,
    ),
    activeGroupId: group.id,
  };
}

export function activateDockGroup(layout: DockLayout, groupId: string): DockLayout {
  return findDockGroup(layout.root, groupId) ? { ...layout, activeGroupId: groupId } : layout;
}

export function dockSurface(
  layout: DockLayout,
  input: {
    readonly surfaceId: string;
    readonly targetGroupId: string;
    readonly position: DockPosition;
    readonly newGroupId: string;
    readonly newSplitId: string;
  },
): DockLayout {
  const target = findDockGroup(layout.root, input.targetGroupId);
  if (!target) return layout;

  let root = mapDockGroups(layout.root, (group) => {
    const surfaceIds = group.surfaceIds.filter((id) => id !== input.surfaceId);
    return {
      ...group,
      surfaceIds,
      activeSurfaceId:
        group.activeSurfaceId === input.surfaceId
          ? (surfaceIds.at(-1) ?? null)
          : group.activeSurfaceId,
    };
  });

  const movedTarget = findDockGroup(root, input.targetGroupId);
  if (!movedTarget) return layout;
  if (input.position === "center") {
    root = mapDockGroups(root, (group) =>
      group.id === movedTarget.id
        ? {
            ...group,
            surfaceIds: [...group.surfaceIds, input.surfaceId],
            activeSurfaceId: input.surfaceId,
          }
        : group,
    );
    return normalizeDockLayout(
      { root, activeGroupId: movedTarget.id },
      dockGroups(root).flatMap((group) => group.surfaceIds),
    );
  }

  const newGroup: DockGroupNode = {
    type: "group",
    id: input.newGroupId,
    surfaceIds: [input.surfaceId],
    activeSurfaceId: input.surfaceId,
  };
  const direction: DockDirection =
    input.position === "left" || input.position === "right" ? "horizontal" : "vertical";
  const before = input.position === "left" || input.position === "top";
  const split: DockSplitNode = {
    type: "split",
    id: input.newSplitId,
    direction,
    ratio: 0.5,
    first: before ? newGroup : movedTarget,
    second: before ? movedTarget : newGroup,
  };
  root = replaceDockNode(root, movedTarget.id, split);
  return normalizeDockLayout(
    { root, activeGroupId: newGroup.id },
    dockGroups(root).flatMap((group) => group.surfaceIds),
  );
}

export function resizeDockSplit(layout: DockLayout, splitId: string, ratio: number): DockLayout {
  const update = (node: DockNode): DockNode => {
    if (node.type === "group") return node;
    if (node.id === splitId) return { ...node, ratio: clampDockSplitRatio(ratio) };
    return { ...node, first: update(node.first), second: update(node.second) };
  };
  return { ...layout, root: update(layout.root) };
}

export function validateDockLayout(value: unknown): value is DockLayout {
  const validateNode = (node: unknown): node is DockNode => {
    if (!node || typeof node !== "object" || !("type" in node) || !("id" in node)) return false;
    if (typeof node.id !== "string") return false;
    if (node.type === "group") {
      return (
        "surfaceIds" in node &&
        Array.isArray(node.surfaceIds) &&
        node.surfaceIds.every((id) => typeof id === "string") &&
        "activeSurfaceId" in node &&
        (node.activeSurfaceId === null || typeof node.activeSurfaceId === "string")
      );
    }
    return (
      node.type === "split" &&
      "direction" in node &&
      (node.direction === "horizontal" || node.direction === "vertical") &&
      "ratio" in node &&
      typeof node.ratio === "number" &&
      Number.isFinite(node.ratio) &&
      "first" in node &&
      validateNode(node.first) &&
      "second" in node &&
      validateNode(node.second)
    );
  };
  return (
    !!value &&
    typeof value === "object" &&
    "activeGroupId" in value &&
    typeof value.activeGroupId === "string" &&
    "root" in value &&
    validateNode(value.root)
  );
}
